/**
 * MobileApp - Coupling WNC
 *
 * Dedicated /app page showcasing the Coupling mobile wallet app
 * with WNC-localized content and download links.
 */

import React from 'react';
import { AppStoreBadges, Footer, SEO, organizationSchema, localBusinessSchema, PLAY_STORE_URL, APP_STORE_URL } from '@coupling/ui';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

const features = [
  {
    title: 'Hold Shop Rewards',
    description: 'Customers use the free Coupling app to hold rewards, vouchers, coupons, and gift cards from WNC shops in one place.',
    icon: (
      <svg className="w-8 h-8 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
      </svg>
    ),
  },
  {
    title: 'Pay In Store',
    description: 'Customers can pay and redeem at the counter while you take cards, cash, or a mix on the phone you already have.',
    icon: (
      <svg className="w-8 h-8 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
      </svg>
    ),
  },
  {
    title: 'Share When Allowed',
    description: 'Rewards can move between customers when you allow it, which makes vouchers and gift cards easier to pass along.',
    icon: (
      <svg className="w-8 h-8 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
  {
    title: 'Merchant POS in the same app',
    description: 'The merchant side of the app is the register: Tap to Pay, catalog, mixed checkout, and appointment booking, with Tap to Pay or an optional Stripe reader.',
    icon: (
      <svg className="w-8 h-8 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
  },
];

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'Use Coupling your way', href: '/#product-modes' },
      { label: 'POS workflow', href: '/#pos-workflow' },
      { label: 'Pricing', href: '/#pricing' },
    ],
  },
  {
    title: 'Mobile App',
    links: [
      { label: 'Google Play', href: PLAY_STORE_URL },
      { label: 'App Store', href: APP_STORE_URL },
    ],
  },
  {
    title: 'Network',
    links: [
      { label: 'Coupling (Global)', href: 'https://cplng.org' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Legal',
    links: [{ label: 'Privacy Policy', href: '/privacy' }],
  },
];

export const MobileApp: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Mobile App - ${seoConfig.siteName}`}
        description="Download the Coupling app for WNC: merchants run Tap to Pay, checkout, and booking on the phone they already have; customers hold rewards in the same app."
        canonical={`${seoConfig.siteUrl}/app`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/app`}
        jsonLd={[
          organizationSchema({ name: seoConfig.siteName, url: seoConfig.siteUrl, description: seoConfig.defaultDescription }),
          localBusinessSchema({
            name: seoConfig.siteName,
            url: seoConfig.siteUrl,
            description: seoConfig.defaultDescription,
            address: { addressLocality: 'Asheville', addressRegion: 'NC', addressCountry: 'US' },
            areaServed: 'Western North Carolina',
          }),
        ]}
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-brand-primary via-brand-primary-dark to-neutral-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/90 text-sm font-semibold mb-6">
                Available on Android &amp; iOS
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                One app for the counter{' '}
                <span className="text-brand-secondary">and for the customer</span>
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-lg">
                Merchants run Tap to Pay, catalog, mixed checkout, and appointments from the mobile app. Customers keep rewards, vouchers, and gift cards ready for the next visit. Tap to Pay on your phone, or an optional Stripe reader.
              </p>
              <AppStoreBadges
                playStoreUrl={PLAY_STORE_URL}
                appStoreUrl={APP_STORE_URL}
                size="lg"
              />
            </div>
            <div className="flex justify-center">
              <img
                src="/product/mobile-wallet-cluster.webp"
                alt="Coupling mobile wallet product shot showing balances, token ecosystems, and nearby places to spend"
                className="w-full h-auto max-w-xl rounded-[2rem] shadow-2xl border border-white/20"
                width={1400}
                height={1000}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-[#fffaf4] border-y border-brand-primary/10">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-center">
          <div className="order-2 lg:order-1">
            <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
              Merchant POS and customer wallet
            </span>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold text-neutral-900">
              Tap to Pay and booking for the shop, a simple wallet for customers
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-600">
              Coupling WNC is a merchant system first. Use the same app to take cards, mix cash and rewards, and book appointments. Customers get a free wallet for the rewards you issue.
            </p>
          </div>
          <div className="order-1 lg:order-2 flex justify-center">
            <img
              src="/product/nfc-card-wnc.jpg"
              alt="Optional Coupling WNC gift card visual. Hardware is a demo prop, not a product we sell."
              className="w-full max-w-md rounded-[2rem] shadow-2xl object-cover"
              width={1200}
              height={1200}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              What the app handles for the shop and the customer
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              One download covers the register on your phone and the wallet in your customer&apos;s pocket.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex gap-4 p-6 rounded-xl border border-neutral-100 hover:border-brand-primary/20 hover:shadow-sm transition-all"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-brand-primary/10 flex items-center justify-center">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">{feature.title}</h3>
                  <p className="text-neutral-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-bgSubtle">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Download the Coupling app to run your WNC shop from your phone, and let customers hold the rewards you issue.
          </p>
          <AppStoreBadges
            playStoreUrl={PLAY_STORE_URL}
            appStoreUrl={APP_STORE_URL}
            size="lg"
            layout="row"
            className="justify-center"
          />
        </div>
      </section>

      <Footer
        description="Helping WNC merchants take payments, book appointments, and keep more value in mountain communities."
        columns={footerColumns}
        copyright={`\u00A9 ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
      />
    </div>
  );
};

export default MobileApp;
